import React from 'react';

import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TableFooter,
  TablePagination,
  Avatar,
} from '@mui/material';
import {Delete as DeleteIcon, Groups as GroupsIcon} from '@mui/icons-material';
import {defineMessages, useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {useQueryClient, useMutation, useQuery} from 'react-query';

import API, {Types, VehicleGetProps, useAuth, useCsrfToken} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {ConfirmationDialog, CircleLoader, LoadError, SquareChip, TooltipButton, useErrorHandler} from '@wheeebo/polaris-ui';

import GroupSelector from '@components/form/GroupSelector';

const INITIAL_ITEM_COUNT = 25;

const msgs = defineMessages({
  headerTitle: {
    id: 'pages.member.mission.FlightPathCard.header.title',
    defaultMessage: 'Groups',
  },
  headerSubTitle: {
    id: 'pages.member.mission.FlightPathCard.header.subTitle',
    defaultMessage: 'List of groups that own this vehicle',
  },
  buttonDelete: {
    id: 'pages.member.mission.FlightPathCard.button.delete',
    defaultMessage: 'Delete',
  },
  buttonResendEmail: {
    id: 'pages.member.mission.FlightPathCard.button.resendEmail',
    defaultMessage: 'Resend Email',
  },
  cellName: {
    id: 'pages.member.mission.FlightPathCard.cell.name',
    defaultMessage: 'Name',
  },
  cellRole: {
    id: 'pages.member.mission.FlightPathCard.cell.role',
    defaultMessage: 'Your role',
  },
});

interface RowProps {
  group: Types.Group;
  onDelete: (groupId: Types.UUID) => void;
  onSelect: (groupId: Types.UUID) => void;
}

function Row({group, onDelete, onSelect}: RowProps) {
  const intl = useIntl();
  const isValidMember = group.membership?.invitationStatus === Types.InvitationStatusAccepted;
  return (
    <>
      <TableRow
        hover
        sx={{cursor: 'pointer'}}
        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
          event.stopPropagation();
          if (isValidMember) {
            onSelect(group.id);
          }
        }}>
        <TableCell>
          <Stack direction="row" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Avatar src={group.imageUrl} variant="rounded" sx={{width: 32, height: 32, backgroundColor: 'transparent'}}>
              <GroupsIcon color="disabled" />
            </Avatar>
            <Typography variant="body2">{group.name}</Typography>
          </Stack>
        </TableCell>
        <TableCell>
          <SquareChip label={group.membership?.role || 'NON-MEMBER'} />
        </TableCell>
        <TableCell align="right">
          {isValidMember && group.membership?.role === Types.GroupMemberOwnerRole ? (
            <TooltipButton
              message={intl.formatMessage(msgs.buttonDelete)}
              icon={<DeleteIcon fontSize="small" />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                onDelete(group.id);
              }}
            />
          ) : null}
        </TableCell>
      </TableRow>
    </>
  );
}

interface DeleteGroupVehicleProps {
  groupId: Types.UUID;
  vehicleId: Types.UUID;
}

interface Props {
  vehicleId: Types.UUID;
}

function GroupManagementCard({vehicleId: vehicleId}: Props): JSX.Element {
  const intl = useIntl();
  const {account} = useAuth();
  const {csrfToken} = useCsrfToken();
  const {showSuccess} = useSnackbar();
  const {showAndEmitJsErrorEvent} = useErrorHandler();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [group, setGroup] = React.useState<Types.Group>();
  const [page, setPage] = React.useState(0);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const options: VehicleGetProps = {preload: ['groups', 'group_membership', 'owner']};
  const {data, isError, isLoading} = useQuery(API.Vehicles.getKey(vehicleId, options), () =>
    API.Vehicles.get(vehicleId, options),
  );
  const {mutate: createGroupVehicle} = useMutation<unknown, unknown>(
    () => {
      setCreateDialogOpen(false);
      if (!group) {
        return Promise.reject();
      }
      return API.GroupVehicles.create(csrfToken, group.id, vehicleId);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(API.GroupVehicles.CacheKey);
        showSuccess('Registered a group');
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const {mutate: deleteGroupVehicle} = useMutation<unknown, unknown, DeleteGroupVehicleProps>(
    (props: DeleteGroupVehicleProps) => API.GroupVehicles.delete_(csrfToken, props.groupId, props.vehicleId),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(API.GroupVehicles.CacheKey);
        showSuccess('Unregistered a group');
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  return (
    <Card>
      <CardHeader
        title={intl.formatMessage(msgs.headerTitle)}
        subheader={intl.formatMessage(msgs.headerSubTitle)}
        action={
          account?.id === data?.owner?.id ? <Button onClick={() => setCreateDialogOpen(true)}>Add</Button> : undefined
        }
      />
      <CardContent>
        {isLoading ? <CircleLoader /> : null}
        {isError ? <LoadError /> : null}
        {data?.groups && data.groups.length > 0 ? (
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{intl.formatMessage(msgs.cellName)}</TableCell>
                  <TableCell>{intl.formatMessage(msgs.cellRole)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.groups
                  ?.map((e: Types.GroupVehicle) => e.group)
                  .map((e: Types.Group) => (
                    <Row
                      key={e.id}
                      group={e}
                      onDelete={(groupId: Types.UUID) => deleteGroupVehicle({groupId, vehicleId})}
                      onSelect={(groupId: Types.UUID) => navigate(`/groups/${groupId}`)}
                    />
                  ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    sx={{
                      '.MuiTablePagination-displayedRows': {m: 1},
                    }}
                    backIconButtonProps={{sx: {p: 0}}}
                    nextIconButtonProps={{sx: {p: 0}}}
                    rowsPerPageOptions={[INITIAL_ITEM_COUNT]}
                    count={data?.groups?.length || 0}
                    rowsPerPage={INITIAL_ITEM_COUNT}
                    page={page}
                    SelectProps={{
                      inputProps: {'aria-label': 'rows per page'},
                    }}
                    onPageChange={(_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage)}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2">No record found.</Typography>
        )}
      </CardContent>
      <ConfirmationDialog
        open={createDialogOpen}
        title="Add a group"
        description="Select a group"
        onCancel={() => setCreateDialogOpen(false)}
        onConfirm={() => createGroupVehicle()}>
        <GroupSelector setSelectedGroup={setGroup} excludeGroups={data?.groups?.map((e) => e.group)} />
      </ConfirmationDialog>
    </Card>
  );
}
export default React.memo(GroupManagementCard);
